/* eslint-disable no-console */
import {Col, Form, Input, Select} from 'antd';
import React from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, transformInput} from '../../constants/Common';
import InformativeText from '../../constants/InformativeText';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  AllCap,
  eachWordCapOrAllCap,
  FirstCap
} from '../../utils/FormUtils/FirstCap';
import RegEx from '../../utils/FormUtils/RegEx';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import PhoneNumberInput from '../CustomInputs/PhoneNumberInput/PhoneNumberInput';
import PercentageSlider from '../PercentageSlider/PercentageSlider';

import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {OwnerShipKey, OwnerShipLabel} from './Constants/IP/OwnerShipKeyLabel';
import UserCreationToggle from './UserCreationToggle/UserCreationToggle';

const OwnerShipForm: React.FC<{
  data?: Record<string, unknown>;
  currentStep?: number;
}> = ({data, currentStep}) => {
  const HoldingType = useJSONStore.getState().holdingType;
  const holdingInfo = InformativeText?.Ownership;
  const yearFilter = yearSelect();
  const [searchParam] = useSearchParams();

  return (
    <>
      {currentStep === 0 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={searchParam.get('selectedYear')}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={yearFilter}
                suffixIcon={<YearCalender />}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={OwnerShipKey.HOLDING_NAME}
              label={OwnerShipLabel.HOLDING_NAME}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(OwnerShipLabel.HOLDING_NAME)
                },
                {
                  max: 100,
                  message: `${OwnerShipLabel.HOLDING_NAME} Should not be more than 100 characters`
                },
                {
                  min: 3,
                  message: `${OwnerShipLabel.HOLDING_NAME} Should be more than 2 characters`
                },
                {
                  pattern: RegEx.businessName,
                  message: CommonErrors.noAllowedSpecChar
                }
              ]}
              normalize={(value) => transformInput.removeExtraSpace(value)}
              initialValue={''}
              validateFirst={true}
              hasFeedback
            >
              <Input
                className="input"
                placeholder={`Enter ${OwnerShipLabel.HOLDING_NAME}`}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = eachWordCapOrAllCap(target.value);
                }}
              />
            </Form.Item>
          </Col>
          <UserCreationToggle
            existingEmail={data?.[OwnerShipKey.EMAIL] as string}
            label={OwnerShipLabel.IS_USER_CREATION}
            dependentFields={[
              OwnerShipKey.FIRST_NAME,
              OwnerShipKey.LAST_NAME,
              OwnerShipKey.TELEPHONE,
              OwnerShipKey.EMAIL
            ]}
          >
            <Col className="gutter-row">
              <Form.Item
                name={OwnerShipKey.FIRST_NAME}
                label={OwnerShipLabel.FIRST_NAME}
                labelCol={{span: 24}}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(OwnerShipLabel.FIRST_NAME)
                  },
                  {
                    max: 100,
                    message: `${OwnerShipLabel.FIRST_NAME} Should not be more than 100 Characters`
                  },
                  {
                    min: 2,
                    message: `${OwnerShipLabel.FIRST_NAME} Should be more than 1 Characters`
                  },
                  {
                    pattern: RegEx.names,
                    message:
                      'No special characters , Empty spaces or Numbers are Allowed'
                  }
                ]}
                validateFirst={true}
                hasFeedback
                initialValue={''}
              >
                <Input
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = FirstCap(target.value);
                  }}
                  placeholder={`Enter ${OwnerShipLabel.FIRST_NAME}`}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row">
              <Form.Item
                name={OwnerShipKey.LAST_NAME}
                label={OwnerShipLabel.LAST_NAME}
                labelCol={{span: 24}}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(OwnerShipLabel.LAST_NAME)
                  },
                  {
                    max: 100,
                    message: `${OwnerShipLabel.LAST_NAME} Should not be more than 100 Characters`
                  },
                  {
                    min: 2,
                    message: `${OwnerShipLabel.LAST_NAME} Should be more than 1 Characters`
                  },
                  {
                    pattern: RegEx.names,
                    message:
                      'No special characters , Empty spaces or Numbers are Allowed'
                  }
                ]}
                validateFirst={true}
                hasFeedback
                initialValue={''}
              >
                <Input
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = FirstCap(target.value);
                  }}
                  placeholder={`Enter ${OwnerShipLabel.LAST_NAME}`}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row">
              <Form.Item
                name={OwnerShipKey.TELEPHONE}
                label={OwnerShipLabel.TELEPHONE}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(OwnerShipLabel.TELEPHONE)
                  },
                  {
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    validator(_, value) {
                      if (RegEx.mobile.test(value)) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject(
                          new Error(
                            CommonErrors.validField(OwnerShipLabel.TELEPHONE)
                          )
                        );
                      }
                    }
                  }
                ]}
                initialValue={''}
                validateFirst={true}
                hasFeedback
                labelCol={{span: 24}}
              >
                <PhoneNumberInput />
              </Form.Item>
            </Col>
            <Col className="gutter-row">
              <Form.Item
                name={OwnerShipKey.EMAIL}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(OwnerShipLabel.EMAIL)
                  },
                  {
                    min: 5,
                    message: `${OwnerShipLabel.EMAIL} Should be more than 4 Characters`
                  },
                  {
                    max: 320,
                    message: `${OwnerShipLabel.EMAIL} Should not be more than 320 Characters`
                  },
                  {
                    pattern: RegEx.email,
                    message: CommonErrors.validField(OwnerShipLabel.EMAIL)
                  }
                ]}
                label={OwnerShipLabel.EMAIL}
                labelCol={{span: 24}}
                validateFirst={true}
                hasFeedback
              >
                <Input
                  className="input"
                  placeholder={`Enter ${OwnerShipLabel.EMAIL}`}
                />
              </Form.Item>
            </Col>
          </UserCreationToggle>
          <Col className="gutter-row">
            <Form.Item
              name={OwnerShipKey.HOLDING_TYPE}
              label={OwnerShipLabel.HOLDING_TYPE}
              labelCol={{span: 24}}
              initialValue={null}
              validateFirst={true}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(OwnerShipLabel.HOLDING_TYPE)
                }
              ]}
            >
              <Select
                className="select"
                placeholder={`Choose ${OwnerShipLabel.HOLDING_TYPE}`}
                options={HoldingType}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <PercentageSlider
              fieldKey={OwnerShipKey.HOLDING_PERCENT}
              fieldLabel={OwnerShipLabel.HOLDING_PERCENT}
              tooltipTitle={holdingInfo?.holding_percent}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(
                    OwnerShipLabel.HOLDING_PERCENT
                  )
                }
              ]}
            />
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={OwnerShipKey.GST}
              label={OwnerShipLabel.GST}
              labelCol={{span: 24}}
              initialValue={''}
              rules={[
                {
                  pattern: RegEx.GST,
                  message: CommonErrors.validField(OwnerShipLabel.GST)
                }
              ]}
              validateFirst={true}
              hasFeedback
            >
              <Input
                className="input"
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = AllCap(target.value);
                }}
                placeholder="00XXXXX0000X0X0/X"
              />
            </Form.Item>
          </Col>
        </>
      )}
    </>
  );
};

export default OwnerShipForm;
